import { useCallback, useSyncExternalStore } from 'react'
import { posthog, isPostHogEnabled } from './posthog'
import { readDevFlagOverride, type DevFlagKey } from './feature-flags'

/**
 * Dev-tool flags (DEV_FLAGS): off unless PostHog explicitly says on.
 *
 *   override (ett:flags, Vite dev server only) → its value
 *   PostHog not configured                     → off
 *   flags not delivered yet / unreachable      → off
 *   delivered                                  → the flag's value (missing = off)
 */
export interface DevFlagInputs {
  /** The `ett:flags` value, already null outside the dev server. */
  override: boolean | null
  posthogConfigured: boolean
  /** `posthog.isFeatureEnabled(key)`: undefined until flags load. */
  posthogValue: boolean | undefined
}

export function resolveDevFlag({
  override,
  posthogConfigured,
  posthogValue,
}: DevFlagInputs): boolean {
  if (override != null) return override
  if (!posthogConfigured) return false
  return posthogValue === true
}

interface DevFlagOptions {
  /** Whether the `ett:flags` override is honoured. Test seam; defaults to the Vite dev server. */
  dev?: boolean
}

function read(key: DevFlagKey, dev: boolean): boolean {
  const configured = isPostHogEnabled()
  return resolveDevFlag({
    override: readDevFlagOverride(key, undefined, dev),
    posthogConfigured: configured,
    // send_event: false: read on every render and flag reload, and each
    // $feature_flag_called event is billed.
    posthogValue: configured ? posthog.isFeatureEnabled(key, { send_event: false }) : undefined,
  })
}

/**
 * For code that cannot call a hook. It does not subscribe: read it at the
 * moment of use, never at module load (PostHog has not answered yet).
 */
export function isDevFlagOn(key: DevFlagKey, opts: DevFlagOptions = {}): boolean {
  return read(key, opts.dev ?? import.meta.env.DEV)
}

/** The flag; re-renders when PostHog delivers or reloads flags. */
export function useDevFlag(key: DevFlagKey, opts: DevFlagOptions = {}): boolean {
  const dev = opts.dev ?? import.meta.env.DEV
  const subscribe = useCallback((cb: () => void) => {
    if (!isPostHogEnabled()) return () => {}
    return posthog.onFeatureFlags(cb)
  }, [])
  const getSnapshot = useCallback(() => read(key, dev), [key, dev])
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
